import { AccountingPayableObjectTypeEnum } from "#graphql/supergraphEnums";
import { GetAccountingPayableForSlideoutQuery, Scalars } from "#graphql/supergraphTypes";
import { formatCalendarDate } from "#utils/datetime_utils";
import { toDbId } from "../../utils/graphql_utils";
import { DEFAULT_BASE_MONEY_PROPS } from "../Money/constants";
import { MoneyType } from "../Money/types";
import { buildTransactionUrl, payableObjectTypeToObjectType } from "../shared/transactionUrl";
import { AccountingPayableData } from "./types";

export function transformQueryResultToPayable(
  data: GetAccountingPayableForSlideoutQuery | undefined,
): AccountingPayableData | null {
  if (!data?.accountingPayable) return null;

  const payable = data.accountingPayable;

  const totalMonies = payable.totalAmountPayable.monies;
  const markedPaidMonies = payable.amountMarkedPaid.monies;
  const automatedPayments = payable.amountPaidViaAutomatedPayments;

  const hostName = payable.arena?.jsqHostname ?? null;
  const entityGlobalId = payable.entity?.id;
  const entityDbId = entityGlobalId ? toDbId(entityGlobalId) : null;
  const entityUrl =
    hostName && entityDbId != null ? `https://${hostName}/account/${entityDbId}` : null;
  const objectType = payable.objectType as AccountingPayableObjectTypeEnum;
  const paymentObjectType = payableObjectTypeToObjectType(objectType);
  const transactionUrl =
    hostName && paymentObjectType
      ? buildTransactionUrl(paymentObjectType, payable.id, hostName)
      : null;

  const singlePosition =
    payable.payeeCount === 1 && payable.payees.length === 1 ? payable.payees[0].position : null;
  const singlePositionDbId = singlePosition?.id ? toDbId(singlePosition.id) : null;
  const singlePositionName = singlePosition?.account?.name ?? null;
  const singlePositionUrl =
    hostName && singlePositionDbId != null
      ? `https://${hostName}/position/${singlePositionDbId}`
      : null;

  return {
    id: payable.id,
    objectType,
    label: payable.label,
    subtitleLabel: payable.subtitleLabel ?? null,
    description: payable.description ?? "",
    date: payable.date,
    clientName: payable.arena?.name ?? "",
    entityId: payable.entityId,
    entityName: payable.entity?.name ?? "",
    totalAmountPayable: totalMonies,
    amountMarkedPaid: markedPaidMonies,
    isPaymentRequested: payable.isPaymentRequested,
    requestedPaymentDate: payable.requestedPaymentDate ?? null,
    payeeCount: payable.payeeCount,
    amountPaidViaAutomatedPayments: automatedPayments ?? DEFAULT_BASE_MONEY_PROPS,
    remainingToPay: computeRemainingToPay(totalMonies, markedPaidMonies, automatedPayments),
    transactionUrl,
    entityUrl,
    singlePositionName,
    singlePositionUrl,
  };
}

export function computeRemainingToPay(
  totalMonies: MoneyType[],
  markedPaidMonies: MoneyType[],
  automatedPayments: Scalars["Money"]["output"] | null,
): MoneyType {
  const automated = automatedPayments ?? DEFAULT_BASE_MONEY_PROPS;
  const totalAmount = totalMonies[0]?.amount ?? 0;
  const markedPaidAmount = markedPaidMonies[0]?.amount ?? 0;
  const automatedAmount = automated.amount;
  const currency = totalMonies[0]?.currency ?? automated.currency;
  const precision = totalMonies[0]?.precision ?? automated.precision;

  return {
    amount: totalAmount - markedPaidAmount - automatedAmount,
    currency,
    precision,
  };
}

export function formatDate(dateStr: string | null | undefined): string {
  return formatCalendarDate(dateStr) ?? "";
}
